import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { bind } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { ReactEditorViewContext } from '@atlaskit/editor-common/ui-react';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { token } from '@atlaskit/tokens';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { remixReadyMessages } from '@atlassian/malleable-ui-components/remix/remix-ready-toast';
import { RemixThinkingBar } from '@atlassian/malleable-ui-components/remix/remix-thinking-bar';
import type {
	AddRemixHistoryEntryPayload,
	AddRemixHistoryStackEntryPayload,
	RemixFinalizeFailedPayload,
} from '@atlassian/malleable-ui-components/types';
import { RovoThinkingBarWrapper } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingBarWrapper';
import { RovoThinkingReady } from '@atlassian/rovo-platform-ui-components/loading-state/RovoThinkingReady';

import { makeAddRemixHistoryEntryCommand } from '../../editor-commands/addRemixHistoryEntry';
import { makeAddRemixHistoryStackEntryCommand } from '../../editor-commands/addRemixHistoryStackEntry';
import { clearRemixThinkingDecoration } from '../../editor-commands/clear-remix-thinking-decoration';
import { markRemixWhiteboardNeedsSpaceSelection } from '../../editor-commands/mark-remix-whiteboard-needs-space-selection';
import { openMauiEmbedSpacePickerCommand } from '../../editor-commands/maui-embed-space-picker';
import { setRemixThinkingComplete } from '../../editor-commands/set-remix-thinking-complete';
import { setShowLocationPickerEditorCommand } from '../../editor-commands/set-show-location-picker';
import { onStartRemixThinking } from '../../editor-commands/start-remix-thinking';
import type { MalleableUiPlugin, PluginConfiguration } from '../../malleableUiPluginType';
import {
	MauiDiagramPasteFlagType,
	MauiEmbedFinalizeStatus,
} from '../../pm-plugins/maui-diagram-paste-plugin';
import { getRemixThinkingWidgetContainer } from '../../pm-plugins/remix-thinking/remix-thinking-decoration';
import { useThinkingWidgetViewportPlacement } from '../hooks/useIsThinkingWidgetInViewport';
import { messages } from '../messages';
import {
	cancelRemixThinking,
	getAbortRequest,
	onRemixThinkingCompleteAnimationFinished,
	registerAbortRequest,
} from '../utils/remix-state';

const styles = cssMap({
	// Floats a bar horizontally-centred against the bottom edge of the viewport,
	// matching the position of the other floating thinking/ready bars (and the
	// legacy toast).
	floatingBarBottom: {
		position: 'fixed',
		bottom: token('space.200'),
		left: '50%',
		transform: 'translateX(-50%)',
		width: 'fit-content',
		// 400px matches the shared Rovo floating ready bar design minimum; no ADS sizing token maps to it.
		minWidth: '400px',
		zIndex: 600,
	},
});

// How long the floating "ready" bar stays visible while the remix is out of
// view before auto-dismissing (per the Remix Figma spec).
const READY_BAR_AUTO_DISMISS_MS = 10000;

interface RemixContentProps {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	config: PluginConfiguration;
}

export const RemixContent = ({ api, config }: RemixContentProps): React.JSX.Element | null => {
	const { createRemixContentComponent } = config;
	const { editorView } = useContext(ReactEditorViewContext);
	const { formatMessage } = useIntl();

	const isViewMode = useSharedPluginStateWithSelector(
		api,
		['editorViewMode'],
		(states) => states.editorViewModeState?.mode === 'view',
	);

	const isModalOpen = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isRemixModalOpen || false,
	);

	const isThinking = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isThinking || false,
	);

	const isComplete = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isComplete || false,
	);

	const remixModalMetadata = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixModalMetadata,
	);

	const remixSource = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.remixSource,
	);

	const showLocationPicker = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.showLocationPicker || false,
	);

	const isUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.isUpdate || false,
	);

	const historyEntryForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.historyEntryForUpdate,
	);

	const dynamicUiSubtypeForUpdate = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.dynamicUiSubtypeForUpdate,
	);

	const savedSelection = useSharedPluginStateWithSelector(
		api,
		['malleableUi'],
		(states) => states.malleableUiState?.savedSelection,
	);

	// Start Remix experience for floating toolbar entry point (can't use hooks in toolbar config).
	const experienceTracker = useAIMateExperienceTracker();
	const prevIsModalOpen = useRef(false);
	useEffect(() => {
		if (!prevIsModalOpen.current && isModalOpen && remixSource === 'floatingToolbar') {
			experienceTracker.start({
				name: ExperienceName.REMIX_START,
				id: ExperienceName.REMIX_START,
				attributes: {
					entryPoint: 'editor',
					remixSource: 'floatingToolbar',
					product: 'confluence',
				},
			});
		}
		prevIsModalOpen.current = isModalOpen;
	}, [isModalOpen, remixSource, experienceTracker]);
	const Component = useMemo(() => createRemixContentComponent(), [createRemixContentComponent]);

	const handleModalClose = useCallback(() => {
		api?.core.actions.execute(api?.malleableUi.commands.closeRemixModal());
	}, [api]);

	const onStartThinking = useCallback(
		(options?: { contentType?: string }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				onStartRemixThinking({ api, options });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(true);
				api?.core.actions.execute(api?.malleableUi.commands.startRemixThinking(options));
			}
		},
		[api],
	);

	const onStopThinking = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				// Abort before cancelling so send-message-action.ts fires the paired
				// aiInteraction initiated + dismissed events via AbortError.
				getAbortRequest(api)?.();
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onDismissThinkingUi = useCallback(
		(options?: { reopenModal?: boolean }) => {
			if (fg('platform_editor_ai_remix_toolbar')) {
				cancelRemixThinking({ api, reopenModal: options?.reopenModal });
			} else {
				api?.aiExperience?.actions.toggleAiEntryPoints?.(false);
				api?.core.actions.execute(api?.malleableUi.commands.stopRemixThinking(options));
			}
		},
		[api],
	);

	const onRegisterAbortRequest = useCallback(
		(abortFn: () => void) => registerAbortRequest(api, abortFn),
		[api],
	);

	const clearThinkingDecoration = useCallback(() => {
		api?.core.actions.execute(clearRemixThinkingDecoration());
	}, [api]);

	const replaceWhiteboardEmbed = useCallback(
		(options: { contentId: string; finalizedUrl: string; localId?: string }) => {
			api?.core.actions.execute(api?.malleableUi.commands.replaceWhiteboardEmbed(options));
		},
		[api],
	);

	// Maps the MAUI-side string status to the editor's finalize-status enum.
	const setMauiEmbedFinalizeStatus = useCallback(
		(options: { contentId: string; status: 'suppressed' | 'needs-finalize' }) => {
			api?.core.actions.execute(
				api?.malleableUi.commands.setMauiEmbedFinalizeStatus({
					contentId: options.contentId,
					status:
						options.status === 'suppressed'
							? MauiEmbedFinalizeStatus.CHECKING
							: MauiEmbedFinalizeStatus.NEEDS_FINALIZE,
				}),
			);
		},
		[api],
	);

	const setThinkingComplete = useCallback(
		(isComplete: boolean) => {
			// this is to clear the inline-Rovo streaming target when the remix is complete
			if (isComplete) {
				api?.aiExperience?.actions?.endInlineRovoStreaming?.();
			}
			api?.core.actions.execute(setRemixThinkingComplete(isComplete));
			if (expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)) {
				// View mode owns the floating thinking bar; let that bar finish its completion
				// animation and open the preview before stopping thinking.
				if (isComplete && !isViewMode) {
					onRemixThinkingCompleteAnimationFinished({ api });
				}
			} else {
				if (isComplete) {
					onRemixThinkingCompleteAnimationFinished({ api });
				}
			}
		},
		[api, isViewMode],
	);

	const _onRemixThinkingCompleteAnimationFinished = useCallback(() => {
		onRemixThinkingCompleteAnimationFinished({ api });
	}, [api]);

	const setShowLocationPicker = useCallback(
		(showLocationPicker: boolean) => {
			api?.core.actions.execute(setShowLocationPickerEditorCommand(showLocationPicker));
		},
		[api],
	);

	// When automatic finalization of a remixed whiteboard fails, open the shared
	// native-embed space picker (the same UI used by the paste / in-embed banner
	// flow) instead of the platform's remix-only picker. We dispatch two separate
	// transactions: first register the embed as NEEDS_SPACE_SELECTION (carrying
	// the "Choose where to save whiteboard" flag), then open the picker. Returns
	// `true` so the platform skips its own fallback flag + picker.
	const onFinalizeFailed = useCallback(
		({ contentId, originalUrl }: RemixFinalizeFailedPayload): boolean => {
			if (!api) {
				return false;
			}

			api.core.actions.execute(
				markRemixWhiteboardNeedsSpaceSelection({
					contentId,
					originalUrl,
					flag: {
						id: `maui-embed-finalize-space-picker-${contentId}`,
						type: MauiDiagramPasteFlagType.INFO,
						title: formatMessage(messages.flagFinalizeSpacePickerTitle),
						description: formatMessage(messages.flagFinalizeSpacePickerDescription),
					},
				}),
			);
			api.core.actions.execute(openMauiEmbedSpacePickerCommand({ contentId, originalUrl }));

			return true;
		},
		[api, formatMessage],
	);

	const addHistoryEntry = useCallback(
		(options: AddRemixHistoryEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryEntryCommand(options));
		},
		[api],
	);

	const addHistoryStackEntry = useCallback(
		(options: AddRemixHistoryStackEntryPayload) => {
			api?.core.actions.execute(makeAddRemixHistoryStackEntryCommand(options));
		},
		[api],
	);

	// Bridges the plugin's `trackVariantUpdateComplete` command into the
	// remix flow; `useOnComplete` fires it once the FE insertion settles.
	const onVariantUpdateComplete = useCallback(
		(id: string) => {
			if (!api?.malleableUi?.commands.trackVariantUpdateComplete) {
				return;
			}
			api.core.actions.execute(api.malleableUi.commands.trackVariantUpdateComplete({ id }));
		},
		[api],
	);

	// Shows the "Output adjusted" flag via editor-level plugin state, which
	// survives this component unmounting on completion.
	const onSubtypeMismatch = useCallback(() => {
		if (!api?.malleableUi?.commands.showSubtypeAdjustedFlag) {
			return;
		}
		api.core.actions.execute(api.malleableUi.commands.showSubtypeAdjustedFlag());
	}, [api]);

	// Track where the inline thinking widget sits relative to the viewport so we
	// can float a bar at the matching edge (top/bottom) whenever it scrolls out
	// of view — during both the thinking phase and the completed "ready" phase.
	// Gated so we don't resolve DOM or bind scroll/resize listeners when the
	// in-editor loading feature is off.
	const floatingBarPlacement = useThinkingWidgetViewportPlacement({
		editorView,
		isActive: (isThinking || isComplete) && fg('platform_editor_ai_remix_in_editor_loading'),
	});
	const isRemixReady =
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		fg('platform_editor_ai_remix_in_editor_loading');

	const handleReadyToastView = useCallback(() => {
		if (editorView) {
			const container = getRemixThinkingWidgetContainer(editorView);
			const target =
				(savedSelection?.from !== undefined && editorView.nodeDOM(savedSelection.from)) ||
				container ||
				(() => {
					try {
						if (!savedSelection?.to) {
							return null;
						}
						const resolved = editorView.state.doc.resolve(savedSelection.to);
						const anchorPos = resolved.depth >= 1 ? resolved.after(1) : savedSelection.to;
						const domAtPos = editorView.domAtPos(anchorPos);
						return domAtPos.node instanceof HTMLElement
							? domAtPos.node
							: domAtPos.node.parentElement;
					} catch {
						return null;
					}
				})();
			if (target instanceof HTMLElement) {
				target.scrollIntoView({
					behavior: 'smooth',
					block: expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)
						? 'start'
						: 'center',
				});
			}
		}
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [editorView, savedSelection?.from, savedSelection?.to, setThinkingComplete]);

	const handleReadyToastClose = useCallback(() => {
		// setThinkingComplete(false); do the job, but a reset could be more meaningful
		setThinkingComplete(false);
	}, [setThinkingComplete]);

	// Only scroll on the rising edge of isRemixReady — not on later 'in-view'
	// transitions from the user scrolling the result into view, which would
	// hijack their scroll.
	const prevIsRemixReadyRef = useRef(false);
	useEffect(() => {
		const justBecameReady = isRemixReady && !prevIsRemixReadyRef.current;
		prevIsRemixReadyRef.current = isRemixReady;

		if (
			!justBecameReady ||
			!editorView ||
			floatingBarPlacement !== 'in-view' ||
			!expValEquals('cc_maui_polish_changes_batch_1', 'isEnabled', true)
		) {
			return;
		}

		const animationFrameId = requestAnimationFrame(handleReadyToastView);
		return () => cancelAnimationFrame(animationFrameId);
	}, [editorView, floatingBarPlacement, handleReadyToastView, isRemixReady]);

	// handle escape key to stop remix thinking
	useEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}

		if (!isThinking || !editorView) {
			return;
		}

		// focus editor first
		if (!editorView.hasFocus()) {
			editorView.focus();
		}

		return bind(editorView.dom, {
			type: 'keydown',
			listener: (e: KeyboardEvent) => {
				if (e.key === 'Escape') {
					e.stopPropagation();

					if (fg('platform_editor_ai_remix_toolbar')) {
						// Abort before cancelling so send-message-action.ts fires the paired
						// aiInteraction initiated + dismissed events via AbortError.
						getAbortRequest(api)?.();
						cancelRemixThinking({ api });
					} else {
						if (
							expValEqualsNoExposure(
								'cc_page_experiences_editor_image_generation',
								'isEnabled',
								true,
							)
						) {
							// If a pre-supplied userPrompt was cancelled (e.g. image generation flow),
							// call the onCancel callback instead of reopening the remix modal.
							const currentMetadata =
								api?.malleableUi?.sharedState.currentState()?.remixModalMetadata;
							const cancelledPrompt = currentMetadata?.userPrompt;
							const onCancel = currentMetadata?.onCancel;
							if (cancelledPrompt && onCancel) {
								onStopThinking({ reopenModal: false });
								onCancel();
								return;
							}
						}
						onStopThinking({
							reopenModal: fg('platform_editor_ai_remix_toolbar') ? !isUpdate : true,
						});
					}
				}
			},
		});
	}, [isThinking, onStopThinking, editorView, api?.malleableUi.sharedState, isUpdate, api]);

	// When the remix modal overlay is open (isModalOpen),
	// set the editor user intent to 'overlayOpen' so the floating toolbar is suppressed
	// (it would otherwise appear above the overlay in Jira).
	// We intentionally do NOT suppress during the thinking-only state
	// — the user may still interact with the editor while the AI is thinking.
	// Reset to 'default' when the modal closes or the component unmounts.
	useLayoutEffect(() => {
		if (!fg('platform_editor_ai_remix_in_editor_loading')) {
			return;
		}
		if (!isModalOpen) {
			if (api?.userIntent?.sharedState.currentState()?.currentUserIntent === 'overlayOpen') {
				api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
			}
			return;
		}
		api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('overlayOpen'));
		return () => {
			api?.core?.actions.execute(api?.userIntent?.commands.setCurrentUserIntent('default'));
		};
	}, [isModalOpen, api]);

	// Auto-dismiss the floating "ready" bar after a fixed delay when the remix is
	// complete but out of view (per the Figma spec). Resetting thinking-complete
	// removes the bar; if the user scrolls back to their remix (placement becomes
	// 'in-view') the bar is hidden anyway and the timer is cleared.
	const isFloatingReadyBarVisible =
		!isViewMode &&
		isComplete &&
		!isThinking &&
		!isModalOpen &&
		floatingBarPlacement !== 'in-view' &&
		fg('platform_editor_ai_remix_in_editor_loading');
	useEffect(() => {
		if (!isFloatingReadyBarVisible) {
			return;
		}
		const timeoutId = setTimeout(() => {
			setThinkingComplete(false);
		}, READY_BAR_AUTO_DISMISS_MS);
		return () => {
			clearTimeout(timeoutId);
		};
	}, [isFloatingReadyBarVisible, setThinkingComplete]);

	if (!api || !Component) {
		return null;
	}

	const selectedContext = api?.aiExperience?.actions.getAISelectionContext();
	if (!selectedContext) {
		return null;
	}

	// when the remix isComplete and
	// - the modal is not open because isComplete may not be reset to false when the modal is open
	// - not thinking because we need to show the thinking bar when isThinking and isComplete
	//   - after onCompleteAnimationFinished, isThinking will be false and only isComplete will be true
	// we need to show the ready toast
	if (isRemixReady) {
		// Surface the shared Rovo "ready" state as a floating bar at the edge of the
		// viewport (closing the inline thinking bar). Only show it when the inline
		// widget is scrolled out of view so it can take the user back to their remix.
		if (floatingBarPlacement === 'in-view') {
			return null;
		}
		return (
			<Box xcss={styles.floatingBarBottom}>
				<RovoThinkingBarWrapper>
					<RovoThinkingReady
						onView={handleReadyToastView}
						onClose={handleReadyToastClose}
						readyText={formatMessage(
							isUpdate
								? remixReadyMessages.updateReady
								: // cc_maui_create_keyword: when enabled, swap the "Remix" keyword for "Create" so the
									// ready toast doesn't leak the legacy copy. Use the no-exposure variant since the
									// create-keyword exposure has already fired upstream when the remix flow started.
									expValEqualsNoExposure('cc_maui_create_keyword', 'isEnabled', true)
									? remixReadyMessages.contentReady
									: remixReadyMessages.remixReady,
						)}
					/>
				</RovoThinkingBarWrapper>
			</Box>
		);
	}

	// When the flag is ON, only show when isModalOpen, isThinking, or showLocationPicker — this
	// is the correct gating behaviour for the new loading path.
	// When the flag is OFF (legacy path), always show — EXCEPT when a pre-supplied userPrompt
	// is present (image-gen flow) and the flow is done (neither isModalOpen nor isThinking),
	// to prevent the option-picker from flashing on cancellation.
	const hasPreSuppliedPrompt = !!remixModalMetadata?.userPrompt;
	const canShow =
		isModalOpen ||
		isThinking ||
		showLocationPicker ||
		(!fg('platform_editor_ai_remix_in_editor_loading') && !hasPreSuppliedPrompt);

	// While the remix is generating, surface a floating thinking bar at the edge
	// of the viewport whenever the inline thinking widget is scrolled out of view,
	// so the user stays aware the process is ongoing. It sticks to the bottom when
	// the widget is above the viewport, or the top when it is below. Rendered
	// alongside the modal/option-picker Component (not instead of it) so any
	// underlying streaming/staging state is never unmounted.
	//
	// During an update (edit of an existing remix), the RemixContainer inside
	// `Component` already renders its own fixed thinking bar (its `hideThinkingBar`
	// guard is `isThinking && !isUpdate`, so it is only hidden for fresh inserts).
	// Rendering this floating bar as well would produce two overlapping, slightly
	// offset bars, so it is gated off for updates to keep a single loading bar.
	const showFloatingThinkingBar =
		!isViewMode &&
		isThinking &&
		!isUpdate &&
		!isModalOpen &&
		floatingBarPlacement !== 'in-view' &&
		fg('platform_editor_ai_remix_in_editor_loading');

	const floatingThinkingBar = showFloatingThinkingBar ? (
		<Box xcss={styles.floatingBarBottom}>
			<RemixThinkingBar isRelative onStop={() => onStopThinking({ reopenModal: !isUpdate })} />
		</Box>
	) : null;

	if (!canShow) {
		return floatingThinkingBar;
	}

	return (
		<React.Fragment>
			{floatingThinkingBar}
			<Component
				remixType="block"
				isViewMode={fg('platform_editor_ai_remix_toolbar') ? isViewMode : undefined}
				isModalOpen={isModalOpen}
				isThinking={isThinking}
				isUpdate={fg('platform_editor_ai_remix_toolbar') ? isUpdate : undefined}
				historyEntryForUpdate={
					fg('platform_editor_ai_remix_toolbar') ? historyEntryForUpdate : undefined
				}
				dynamicUiSubtypeForUpdate={
					fg('platform_editor_ai_remix_toolbar') ? dynamicUiSubtypeForUpdate : undefined
				}
				isComplete={fg('platform_editor_ai_remix_in_editor_loading') ? isComplete : undefined}
				handleModalClose={handleModalClose}
				remixSource={remixSource}
				selectedContext={selectedContext}
				editorApi={api}
				clearThinkingDecoration={
					fg('platform_editor_ai_remix_in_editor_loading') ? clearThinkingDecoration : undefined
				}
				replaceWhiteboardEmbed={
					fg('platform_editor_ai_remix_in_editor_loading') ? replaceWhiteboardEmbed : undefined
				}
				setMauiEmbedFinalizeStatus={
					fg('platform_editor_ai_remix_in_editor_loading') ? setMauiEmbedFinalizeStatus : undefined
				}
				setThinkingComplete={
					fg('platform_editor_ai_remix_in_editor_loading') ? setThinkingComplete : undefined
				}
				onStartThinking={onStartThinking}
				onDismissThinkingUi={onDismissThinkingUi}
				onStopThinking={onStopThinking}
				onRegisterAbortRequest={onRegisterAbortRequest}
				onRemixThinkingCompleteAnimationFinished={
					fg('platform_editor_ai_remix_toolbar')
						? _onRemixThinkingCompleteAnimationFinished
						: undefined
				}
				showLocationPicker={
					fg('platform_editor_ai_remix_in_editor_loading') ? showLocationPicker : false
				}
				setShowLocationPicker={
					fg('platform_editor_ai_remix_in_editor_loading') ? setShowLocationPicker : () => {}
				}
				onFinalizeFailed={
					fg('platform_editor_ai_remix_in_editor_loading') ? onFinalizeFailed : undefined
				}
				addHistoryEntry={fg('platform_editor_ai_remix_toolbar') ? addHistoryEntry : () => {}}
				addHistoryStackEntry={
					fg('platform_editor_ai_remix_toolbar') ? addHistoryStackEntry : () => {}
				}
				onVariantUpdateComplete={
					fg('platform_editor_ai_remix_toolbar') ? onVariantUpdateComplete : undefined
				}
				onSubtypeMismatch={fg('platform_editor_ai_remix_toolbar') ? onSubtypeMismatch : undefined}
				remixModalMetadata={remixModalMetadata}
				hiddenOptions={config.hiddenOptions}
			/>
		</React.Fragment>
	);
};
